import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircleAlert } from "lucide-react";
import { toast } from "sonner";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { CopyButton } from "~/components/ui/copy-button";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Progress } from "~/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";
import { useFetchAll } from "~/hooks/useFetchAll";
import { calculateDammChecksum } from "~/util/Damm";
import { Routes, getRoute } from "~/routes";
import {
  useAddCassetteEntitlementAdjustmentMutation,
  useCassetteDetailLazyQuery,
  useCassetteDetailQuery,
  useCreateCassetteOverflowRequestMutation,
  useFulfilCassetteOverflowRequestMutation,
  useOpenCassetteOverflowRequestsLazyQuery,
} from "generated/hooks";

interface CassetteWithSerialNumber {
  id?: string;
  serialNumber?: {
    serialNumber: number;
    range?: { productType?: { code?: string | null } | null } | null;
  } | null;
}

/** The printed serial number, or null when none has been assigned. */
const formatCassetteSerialNumber = (
  cassette?: CassetteWithSerialNumber | null
): string | null => {
  const serialNumber = cassette?.serialNumber?.serialNumber;

  if (serialNumber === null || serialNumber === undefined) {
    return null;
  }

  const productCode = cassette?.serialNumber?.range?.productType?.code ?? "";
  const full = `${productCode}${serialNumber.toString().padStart(7, "0")}`;

  return `${full}${calculateDammChecksum(full)}`;
};

const formatCassetteLabel = (cassette?: CassetteWithSerialNumber | null) =>
  formatCassetteSerialNumber(cassette) ??
  (cassette?.id ? `Cassette ${cassette.id.slice(0, 8)}` : "Cassette");

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString() : "—";

const formatDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : "—";

const getRatio = (used: number, total: number) => {
  if (total <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, (used / total) * 100));
};

const formatSignedValue = (value: number) =>
  value > 0 ? `+${value}` : `${value}`;

const CONNECTION_PAGE_SIZE = 25;

type StatusTone = "healthy" | "warning" | "error";

const getCassetteStatus = ({
  remainingCustomerTests,
  remainingPhysicalSensors,
  hasOpenOverflowWarning,
}: {
  remainingCustomerTests: number;
  remainingPhysicalSensors: number;
  hasOpenOverflowWarning: boolean;
}) => {
  if (remainingPhysicalSensors <= 0) {
    return {
      label: "Physically exhausted",
      tone: "error" as StatusTone,
      description: "No remaining physical sensor capacity on this cassette.",
    };
  }

  if (remainingCustomerTests <= 0) {
    return {
      label: "Entitlement exhausted",
      tone: "warning" as StatusTone,
      description:
        "Customer tests are fully used even if some physical contingency remains.",
    };
  }

  if (hasOpenOverflowWarning) {
    return {
      label: "Carry-forward required",
      tone: "warning" as StatusTone,
      description:
        "This cassette still has owed tests that must be fulfilled by a future cassette.",
    };
  }

  return {
    label: "Healthy",
    tone: "healthy" as StatusTone,
    description:
      "This cassette still has customer entitlement and physical capacity available.",
  };
};

const getStatusBadgeVariant = (
  tone: StatusTone
): React.ComponentProps<typeof Badge>["variant"] => {
  switch (tone) {
    case "error":
      return "destructive";
    case "warning":
      return "outline";
    case "healthy":
    default:
      return "secondary";
  }
};

interface GaugeStatProps {
  label: string;
  remaining: number;
  used: number;
  total: number;
}

const GaugeStat: React.FC<GaugeStatProps> = ({
  label,
  remaining,
  used,
  total,
}) => (
  <div className="flex h-full flex-col gap-3 p-6">
    <p className="min-h-8 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <div className="flex items-baseline gap-2">
      <span className="text-3xl font-semibold tabular-nums leading-none">
        {remaining}
      </span>
      <span className="text-sm text-muted-foreground tabular-nums">
        / {total}
      </span>
    </div>
    <Progress value={getRatio(used, total)} className="h-1.5" />
    <p className="mt-auto text-xs text-muted-foreground tabular-nums">
      <span className="font-medium text-foreground">{used}</span> used
    </p>
  </div>
);

interface CapacityYieldStatProps {
  label: string;
  remaining: number;
  good: number;
  bad: number;
  total: number;
}

const CapacityYieldStat: React.FC<CapacityYieldStatProps> = ({
  label,
  remaining,
  good,
  bad,
  total,
}) => {
  const checked = good + bad;
  const yieldPct = checked > 0 ? (good / checked) * 100 : null;
  const goodShare = checked > 0 ? (good / checked) * 100 : 0;
  const badShare = checked > 0 ? (bad / checked) * 100 : 0;

  return (
    <div className="flex h-full flex-col gap-3 p-6">
      <p className="min-h-8 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-semibold tabular-nums leading-none">
          {remaining}
        </span>
        <span className="text-sm text-muted-foreground tabular-nums">
          / {total}
        </span>
      </div>
      {checked > 0 ? (
        <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-emerald-500"
            style={{ width: `${goodShare}%` }}
          />
          <div
            className="h-full bg-rose-500"
            style={{ width: `${badShare}%` }}
          />
        </div>
      ) : (
        <div className="h-1.5 w-full rounded-full bg-muted" />
      )}
      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground tabular-nums">
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-emerald-500" />
          <span className="font-medium text-foreground">{good}</span> good
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-rose-500" />
          <span className="font-medium text-foreground">{bad}</span> bad
        </span>
        {yieldPct !== null ? (
          <span>
            <span
              className={cn(
                "font-medium",
                yieldPct > 90
                  ? "text-emerald-600 dark:text-emerald-400"
                  : yieldPct >= 80
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-rose-600 dark:text-rose-400"
              )}
            >
              {yieldPct.toFixed(1)}%
            </span>{" "}
            yield
          </span>
        ) : null}
      </div>
    </div>
  );
};

interface CountStatProps {
  label: string;
  value: number;
  caption: string;
  emphasise?: boolean;
}

const CountStat: React.FC<CountStatProps> = ({
  label,
  value,
  caption,
  emphasise,
}) => (
  <div className="flex h-full flex-col gap-3 p-6">
    <p className="min-h-8 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <span
      className={cn(
        "text-3xl font-semibold tabular-nums leading-none",
        emphasise && value > 0 && "text-amber-600 dark:text-amber-400"
      )}
    >
      {value}
    </span>
    <p className="mt-auto text-xs text-muted-foreground">{caption}</p>
  </div>
);

interface SectionHeaderProps {
  title: string;
  description?: string;
  eyebrow?: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  description,
  eyebrow,
}) => (
  <div className="flex items-end justify-between gap-4 border-b border-border pb-3">
    <div className="space-y-1">
      {eyebrow ? (
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      {description ? (
        <p className="text-sm text-muted-foreground">{description}</p>
      ) : null}
    </div>
  </div>
);

interface DetailPairProps {
  label: string;
  value: React.ReactNode;
}

const DetailPair: React.FC<DetailPairProps> = ({ label, value }) => (
  <div className="space-y-1">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm font-medium text-foreground">{value}</dd>
  </div>
);

const EmptyTableState: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-md border border-dashed border-border/70 px-4 py-8 text-center text-sm text-muted-foreground">
    {message}
  </div>
);

const ViewCassette: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, loading, error, refetch } = useCassetteDetailQuery({
    variables: {
      id: id!,
      firstEntitlementAdjustments: CONNECTION_PAGE_SIZE,
      afterEntitlementAdjustments: null,
      firstOverflowRequests: CONNECTION_PAGE_SIZE,
      afterOverflowRequests: null,
    },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });
  const [fetchAllEntitlementAdjustments] = useCassetteDetailLazyQuery();
  const [addAdjustment, addAdjustmentResult] =
    useAddCassetteEntitlementAdjustmentMutation();
  const [createOverflowRequest, createOverflowRequestResult] =
    useCreateCassetteOverflowRequestMutation();
  const [fulfilOverflowRequest, fulfilOverflowRequestResult] =
    useFulfilCassetteOverflowRequestMutation();
  const [fetchOpenOverflowRequests] = useOpenCassetteOverflowRequestsLazyQuery();
  const {
    data: allEntitlementAdjustmentNodes,
    refetch: refetchEntitlementAdjustments,
  } = useFetchAll(
    fetchAllEntitlementAdjustments,
    (page) => page.cassette?.entitlementAdjustments,
    {
      skip: !id,
      fetchPolicy: "network-only",
      getVariables: (after) => ({
        id: id!,
        firstEntitlementAdjustments: CONNECTION_PAGE_SIZE,
        afterEntitlementAdjustments: after ?? null,
        firstOverflowRequests: 1,
        afterOverflowRequests: null,
      }),
    }
  );
  const { data: allOpenOverflowRequestNodes } = useFetchAll(
    fetchOpenOverflowRequests,
    (page) => page.openCassetteOverflowRequests,
    {
      skip: !id,
      fetchPolicy: "network-only",
      variables: {
        excludingCassetteId: id ?? null,
        after: null,
      },
    }
  );

  const [creditTests, setCreditTests] = useState("1");
  const [creditNotes, setCreditNotes] = useState("");

  const [overflowTests, setOverflowTests] = useState("1");
  const [overflowNotes, setOverflowNotes] = useState("");

  const [fulfilOverflowRequestId, setFulfilOverflowRequestId] = useState("");
  const [fulfilTests, setFulfilTests] = useState("1");
  const [fulfilNotes, setFulfilNotes] = useState("");

  const cassette = data?.cassette;
  const loadingAction =
    addAdjustmentResult.loading ||
    createOverflowRequestResult.loading ||
    fulfilOverflowRequestResult.loading;

  const fulfilmentOverflowRequests = useMemo(
    () =>
      allOpenOverflowRequestNodes.filter((request) => request.outstandingTests > 0),
    [allOpenOverflowRequestNodes]
  );

  const selectedFulfilmentRequest = useMemo(
    () =>
      fulfilmentOverflowRequests.find(
        (request) => request.id === fulfilOverflowRequestId
      ) ?? null,
    [fulfilmentOverflowRequests, fulfilOverflowRequestId]
  );

  const spareAllocatableTests =
    cassette?.currentCassetteExtraAllocatableTests ?? 0;

  const creditTestsNumber = Number.parseInt(creditTests, 10);
  const canGrant =
    Number.isFinite(creditTestsNumber) &&
    creditTestsNumber > 0 &&
    creditTestsNumber <= spareAllocatableTests;

  const overflowTestsNumber = Number.parseInt(overflowTests, 10);
  const canRecordOverflow =
    Number.isFinite(overflowTestsNumber) && overflowTestsNumber > 0;

  const fulfilTestsNumber = Number.parseInt(fulfilTests, 10);
  const maxFulfillableTests = Math.min(
    selectedFulfilmentRequest?.outstandingTests ?? 0,
    spareAllocatableTests
  );
  const canFulfil =
    Boolean(selectedFulfilmentRequest) &&
    Number.isFinite(fulfilTestsNumber) &&
    fulfilTestsNumber > 0 &&
    fulfilTestsNumber <= maxFulfillableTests;

  const handleFulfilRequestChange = (value: string) => {
    setFulfilOverflowRequestId(value);
    const request = fulfilmentOverflowRequests.find((r) => r.id === value);
    if (request) {
      setFulfilTests(
        String(Math.max(1, Math.min(request.outstandingTests, spareAllocatableTests)))
      );
    }
  };

  const handleGrantExtraTests = async () => {
    if (!cassette?.id) {
      return;
    }

    if (!Number.isFinite(creditTestsNumber) || creditTestsNumber <= 0) {
      toast.error("Enter a positive number of tests to grant.");
      return;
    }

    if (creditTestsNumber > spareAllocatableTests) {
      toast.error(
        `Only ${spareAllocatableTests} extra tests can be allocated to this cassette.`
      );
      return;
    }

    try {
      await addAdjustment({
        variables: {
          input: {
            cassetteId: cassette.id,
            customerTestDelta: creditTestsNumber,
            reasonCode: "MANUAL_CREDIT",
            notes: creditNotes.trim() || null,
          },
        },
      });
      await Promise.all([refetch(), refetchEntitlementAdjustments()]);
      setCreditNotes("");
      toast.success("Cassette tests updated.");
    } catch (mutationError) {
      toast.error(
        mutationError instanceof Error
          ? mutationError.message
          : "Failed to grant extra tests."
      );
    }
  };

  const handleCreateOverflowRequest = async () => {
    if (!cassette?.id) {
      return;
    }

    if (!Number.isFinite(overflowTestsNumber) || overflowTestsNumber <= 0) {
      toast.error("Enter a positive number of tests owed.");
      return;
    }

    try {
      await createOverflowRequest({
        variables: {
          input: {
            cassetteId: cassette.id,
            requestedTests: overflowTestsNumber,
            reasonCode: "CARRY_FORWARD",
            notes: overflowNotes.trim() || null,
          },
        },
      });
      await refetch();
      setOverflowNotes("");
      toast.success("Overflow warning recorded.");
    } catch (mutationError) {
      toast.error(
        mutationError instanceof Error
          ? mutationError.message
          : "Failed to record overflow warning."
      );
    }
  };

  const handleFulfilOverflowRequest = async () => {
    if (!cassette?.id || !selectedFulfilmentRequest) {
      return;
    }

    if (!Number.isFinite(fulfilTestsNumber) || fulfilTestsNumber <= 0) {
      toast.error("Enter a positive number of fulfilled tests.");
      return;
    }

    if (fulfilTestsNumber > maxFulfillableTests) {
      toast.error(
        `Only ${maxFulfillableTests} tests can be fulfilled from this cassette.`
      );
      return;
    }

    try {
      await fulfilOverflowRequest({
        variables: {
          input: {
            overflowRequestId: selectedFulfilmentRequest.id,
            fulfilledByCassetteId: cassette.id,
            fulfilledTests: fulfilTestsNumber,
            notes: fulfilNotes.trim() || null,
          },
        },
      });
      await refetch();
      setFulfilNotes("");
      setFulfilOverflowRequestId("");
      setFulfilTests("1");
      toast.success("Carry-forward fulfilled.");
    } catch (mutationError) {
      toast.error(
        mutationError instanceof Error
          ? mutationError.message
          : "Failed to fulfil carry-forward."
      );
    }
  };

  const pageTitle = cassette ? formatCassetteLabel(cassette) : "Cassette";

  if (error) {
    return (
      <PageTemplate
        title={pageTitle}
        activeItem="Admin"
        activeSubItem="Cassettes"
        breadcrumbs={[
          { label: "Admin", path: Routes.Admin },
          { label: "Cassettes", path: Routes.AdminCassettes },
        ]}
      >
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle>Unable to load cassette</CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
        </Card>
      </PageTemplate>
    );
  }

  if (loading || !id) {
    return (
      <PageTemplate
        title="Cassette"
        activeItem="Admin"
        activeSubItem="Cassettes"
        breadcrumbs={[
          { label: "Admin", path: Routes.Admin },
          { label: "Cassettes", path: Routes.AdminCassettes },
        ]}
      >
        <Card>
          <CardHeader>
            <CardTitle>Loading cassette</CardTitle>
            <CardDescription>Fetching cassette details and support history.</CardDescription>
          </CardHeader>
        </Card>
      </PageTemplate>
    );
  }

  if (!cassette) {
    return (
      <PageTemplate
        title="Cassette"
        activeItem="Admin"
        activeSubItem="Cassettes"
        breadcrumbs={[
          { label: "Admin", path: Routes.Admin },
          { label: "Cassettes", path: Routes.AdminCassettes },
        ]}
      >
        <Card>
          <CardHeader>
            <CardTitle>Cassette not found</CardTitle>
            <CardDescription>The requested cassette could not be loaded.</CardDescription>
          </CardHeader>
        </Card>
      </PageTemplate>
    );
  }

  const cassetteLabel = formatCassetteLabel(cassette);
  const cassetteSerialNumber = formatCassetteSerialNumber(cassette);
  const allocatedCustomerTests = cassette.allocatedCustomerTests;
  const usedCustomerTests = cassette.usedCustomerTests;
  const remainingCustomerTests = cassette.remainingCustomerTests;
  const physicalSensorsUsed = cassette.physicalSensorsUsed;
  const remainingPhysicalSensors = cassette.remainingPhysicalSensors;
  const physicalCapacity = physicalSensorsUsed + remainingPhysicalSensors;
  const goodSensorCount = cassette.goodSensorCount;
  const badSensorCount = cassette.badSensorCount;
  const hasOpenOverflowWarning = cassette.hasOpenOverflowWarning;
  const openOverflowRequestedTests = cassette.openOverflowRequestedTests;
  const status = getCassetteStatus({
    remainingCustomerTests,
    remainingPhysicalSensors,
    hasOpenOverflowWarning,
  });

  const entitlementAdjustments =
    allEntitlementAdjustmentNodes.length > 0
      ? allEntitlementAdjustmentNodes
      : (cassette.entitlementAdjustments?.nodes ?? []).filter(
          (adjustment): adjustment is NonNullable<typeof adjustment> =>
            Boolean(adjustment)
        );

  const hasOpenFulfilmentRequests = fulfilmentOverflowRequests.length > 0;
  const fulfilFieldsDisabled =
    !hasOpenFulfilmentRequests ||
    !selectedFulfilmentRequest ||
    spareAllocatableTests <= 0;

  return (
    <PageTemplate
      title={cassetteLabel}
      subtitle={status.description}
      activeItem="Admin"
      activeSubItem="Cassettes"
      breadcrumbs={[
        { label: "Admin", path: Routes.Admin },
        { label: "Cassettes", path: Routes.AdminCassettes },
        {
          label: cassetteLabel,
          path: id
            ? getRoute(Routes.AdminCassetteDetails, { id })
            : Routes.AdminCassettes,
        },
      ]}
    >
      <div className="space-y-10">
          {/* Status strip: badges + 4 inline stats */}
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={getStatusBadgeVariant(status.tone)}>
                {status.label}
              </Badge>
              {hasOpenOverflowWarning ? (
                <Badge variant="outline">
                  <span className="tabular-nums">
                    {openOverflowRequestedTests}
                  </span>
                  &nbsp;owed forward
                </Badge>
              ) : null}
              <Badge variant="secondary">{cassetteLabel}</Badge>
              {cassetteSerialNumber ? (
                <CopyButton value={cassetteSerialNumber} label="serial number" />
              ) : null}
              {cassette?.id ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  onClick={() =>
                    navigate(
                      `${Routes.PesTests}?cassettes=${encodeURIComponent(
                        cassette.id
                      )}`
                    )
                  }
                >
                  View tests
                </Button>
              ) : null}
            </div>

            <Card className="overflow-hidden p-0">
              <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-2 sm:divide-x lg:grid-cols-4 lg:divide-y-0">
                <GaugeStat
                  label="Customer tests remaining"
                  remaining={remainingCustomerTests}
                  used={usedCustomerTests}
                  total={allocatedCustomerTests}
                />
                <CapacityYieldStat
                  label="Physical sensors remaining"
                  remaining={remainingPhysicalSensors}
                  good={goodSensorCount}
                  bad={badSensorCount}
                  total={physicalCapacity}
                />
                <CountStat
                  label="Spare allocatable"
                  value={spareAllocatableTests}
                  caption="extra credits this cassette can still absorb"
                />
                <CountStat
                  label="Owed forward"
                  value={openOverflowRequestedTests}
                  caption={
                    openOverflowRequestedTests > 0
                      ? "tests owed to a future cassette"
                      : "no open carry-forwards"
                  }
                  emphasise
                />
              </div>
            </Card>
          </div>

          {hasOpenOverflowWarning ? (
            <Alert>
              <CircleAlert className="size-4" />
              <AlertTitle>Carry-forward attention required</AlertTitle>
              <AlertDescription>
                This cassette still owes{" "}
                <strong>{openOverflowRequestedTests}</strong> customer tests
                that need to be fulfilled by a future cassette.
              </AlertDescription>
            </Alert>
          ) : null}

          {/* Actions */}
          <section className="space-y-4">
            <SectionHeader
              eyebrow="Support"
              title="Actions"
              description="Apply adjustments, log a carry-forward, or settle one from another cassette."
            />
            <Card className="overflow-hidden p-0">
              <div className="divide-y divide-border">
                <div className="flex flex-col gap-4 p-6 lg:flex-row lg:items-start lg:gap-8">
                  <div className="space-y-1 lg:w-64 lg:shrink-0 lg:pt-1">
                    <h3 className="text-base font-semibold">
                      Grant extra tests
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Add direct customer entitlement to this cassette.
                    </p>
                    {spareAllocatableTests > 0 ? (
                      <p className="text-xs text-muted-foreground tabular-nums">
                        Up to{" "}
                        <span className="font-medium text-foreground">
                          {spareAllocatableTests}
                        </span>{" "}
                        can be allocated.
                      </p>
                    ) : (
                      <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                        Cassette is at capacity.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-end gap-3 lg:flex-1">
                    <div className="w-28 space-y-1.5">
                      <Label htmlFor="credit-tests">Tests</Label>
                      <Input
                        id="credit-tests"
                        type="number"
                        min={1}
                        max={spareAllocatableTests || undefined}
                        value={creditTests}
                        onChange={(event) =>
                          setCreditTests(event.target.value)
                        }
                        disabled={spareAllocatableTests <= 0}
                      />
                    </div>
                    <div className="min-w-[240px] flex-1 space-y-1.5">
                      <Label htmlFor="credit-notes">Notes</Label>
                      <Input
                        id="credit-notes"
                        value={creditNotes}
                        onChange={(event) => setCreditNotes(event.target.value)}
                        placeholder="Optional support notes"
                      />
                    </div>
                    <Button
                      onClick={handleGrantExtraTests}
                      disabled={loadingAction || !canGrant}
                    >
                      Grant tests
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col gap-4 p-6 lg:flex-row lg:items-start lg:gap-8">
                  <div className="space-y-1 lg:w-64 lg:shrink-0 lg:pt-1">
                    <h3 className="text-base font-semibold">
                      Record owed tests
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Open a carry-forward when this cassette cannot absorb
                      more.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-end gap-3 lg:flex-1">
                    <div className="w-28 space-y-1.5">
                      <Label htmlFor="overflow-tests">Tests owed</Label>
                      <Input
                        id="overflow-tests"
                        type="number"
                        min={1}
                        value={overflowTests}
                        onChange={(event) =>
                          setOverflowTests(event.target.value)
                        }
                      />
                    </div>
                    <div className="min-w-[240px] flex-1 space-y-1.5">
                      <Label htmlFor="overflow-notes">Notes</Label>
                      <Input
                        id="overflow-notes"
                        value={overflowNotes}
                        onChange={(event) =>
                          setOverflowNotes(event.target.value)
                        }
                        placeholder="Why the next cassette must carry extra tests"
                      />
                    </div>
                    <Button
                      onClick={handleCreateOverflowRequest}
                      disabled={loadingAction || !canRecordOverflow}
                      variant="secondary"
                    >
                      Record warning
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col gap-4 p-6 lg:flex-row lg:items-start lg:gap-8">
                  <div className="space-y-1 lg:w-64 lg:shrink-0 lg:pt-1">
                    <h3 className="text-base font-semibold">
                      Fulfil from this cassette
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Settle an older carry-forward from another cassette.
                    </p>
                    {!hasOpenFulfilmentRequests ? (
                      <p className="text-xs text-muted-foreground">
                        No open carry-forwards are available to fulfil.
                      </p>
                    ) : selectedFulfilmentRequest ? (
                      <p className="text-xs text-muted-foreground tabular-nums">
                        <span className="font-medium text-foreground">
                          {maxFulfillableTests}
                        </span>{" "}
                        can be fulfilled from this cassette.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground tabular-nums">
                        <span className="font-medium text-foreground">
                          {fulfilmentOverflowRequests.length}
                        </span>{" "}
                        open{" "}
                        {fulfilmentOverflowRequests.length === 1
                          ? "carry-forward"
                          : "carry-forwards"}{" "}
                        waiting.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-end gap-3 lg:flex-1">
                    <div className="min-w-[280px] flex-1 space-y-1.5">
                      <Label htmlFor="overflow-request-select">
                        Open carry-forward
                      </Label>
                      <Select
                        value={fulfilOverflowRequestId}
                        onValueChange={handleFulfilRequestChange}
                        disabled={!hasOpenFulfilmentRequests}
                      >
                        <SelectTrigger
                          id="overflow-request-select"
                          className="w-full"
                        >
                          <SelectValue
                            placeholder={
                              hasOpenFulfilmentRequests
                                ? "Select a carry-forward"
                                : "No open carry-forwards available"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {fulfilmentOverflowRequests.map((request) => (
                            <SelectItem key={request.id} value={request.id}>
                              {formatCassetteLabel(request.cassette)} ·{" "}
                              {request.outstandingTests} remaining ·{" "}
                              {new Date(request.createdAt).toLocaleDateString()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-28 space-y-1.5">
                      <Label htmlFor="fulfil-tests">Tests</Label>
                      <Input
                        id="fulfil-tests"
                        type="number"
                        min={1}
                        max={maxFulfillableTests || undefined}
                        value={fulfilTests}
                        onChange={(event) => setFulfilTests(event.target.value)}
                        disabled={fulfilFieldsDisabled}
                      />
                    </div>
                    <div className="min-w-[200px] flex-1 space-y-1.5">
                      <Label htmlFor="fulfil-notes">Notes</Label>
                      <Input
                        id="fulfil-notes"
                        value={fulfilNotes}
                        onChange={(event) => setFulfilNotes(event.target.value)}
                        placeholder="Optional fulfilment notes"
                        disabled={fulfilFieldsDisabled}
                      />
                    </div>
                    <Button
                      onClick={handleFulfilOverflowRequest}
                      disabled={loadingAction || !canFulfil}
                    >
                      Fulfil carry-forward
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </section>

          {/* History — entitlement adjustments only */}
          <section className="space-y-4">
            <SectionHeader
              eyebrow="Audit"
              title="Entitlement history"
              description="Base purchase and every later manual adjustment recorded against this cassette."
            />
            <Card>
              <CardContent className="pt-6">
                {entitlementAdjustments.length === 0 ? (
                  <EmptyTableState message="No entitlement adjustments recorded." />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Created</TableHead>
                        <TableHead>Delta</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entitlementAdjustments.map((adjustment) =>
                        adjustment ? (
                          <TableRow key={adjustment.id}>
                            <TableCell className="text-muted-foreground">
                              {formatDateTime(adjustment.createdAt)}
                            </TableCell>
                            <TableCell className="font-medium tabular-nums">
                              {formatSignedValue(adjustment.customerTestDelta)}
                            </TableCell>
                            <TableCell>{adjustment.reasonCode}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {adjustment.notes || "—"}
                            </TableCell>
                          </TableRow>
                        ) : null
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Details — compact metadata strip */}
          <section className="space-y-4 border-t border-border pt-8">
            <h2 className="text-sm font-medium text-muted-foreground">
              Details
            </h2>
            <dl className="flex flex-wrap gap-x-12 gap-y-5">
              <DetailPair label="Cassette label" value={cassetteLabel} />
              <DetailPair
                label="Base purchase"
                value={
                  <span className="tabular-nums">
                    {cassette?.baseCustomerTests ?? 0}
                  </span>
                }
              />
              <DetailPair
                label="Manual adjustments"
                value={
                  <span className="tabular-nums">
                    {formatSignedValue(
                      cassette?.manualCustomerTestAdjustments ?? 0
                    )}
                  </span>
                }
              />
              <DetailPair
                label="Manufactured"
                value={formatDate(cassette?.manufactureDate)}
              />
              <DetailPair
                label="Expiry"
                value={formatDate(cassette?.expiryDate)}
              />
              <DetailPair
                label="Created"
                value={formatDateTime(cassette?.createdAt)}
              />
              <DetailPair
                label="Last updated"
                value={formatDateTime(cassette?.updatedAt)}
              />
            </dl>
          </section>
      </div>
    </PageTemplate>
  );
};

export default ViewCassette;
